<template>
  <v-navigation-drawer
    v-model="localDrawer"
    :permanent="!isMobile"
    :temporary="isMobile"
    app
    class="sidebar"
    width="280"
  >
    <!-- Logo Section -->
    <div class="logo-section pa-4 text-center">
      <v-img src="/logo-2.png" alt="IT Centre Logo" height="19" class="mx-auto mb-3" />
    </div>

    <v-divider class="mx-4 my-2" style="border-color: rgba(255, 255, 255, 0.1)" />

    <!-- Navigation Menu -->
    <v-list nav class="pa-3">
      <v-list-item
        v-for="item in filteredNavigationItems"
        :key="item.value"
        :active="isActive(item.value)"
        @click="handleNavigate(item.value)"
        class="mb-2 rounded-lg nav-item"
        :class="{ 'active-item': isActive(item.value) }"
      >
        <template #prepend>
          <v-icon :color="isActive(item.value) ? 'white' : 'grey-lighten-2'" size="22" class="mr-3">
            {{ item.icon }}
          </v-icon>
        </template>
        <v-list-item-title
          :class="isActive(item.value) ? 'text-white font-weight-bold' : 'text-grey-lighten-1'"
          class="text-body-2"
        >
          {{ item.title }}
        </v-list-item-title>
      </v-list-item>
    </v-list>

    <!-- Footer -->
    <template #append>
      <div class="pa-4">
        <v-divider class="mb-4" style="border-color: rgba(255, 255, 255, 0.1)" />

        <!-- User Info -->
        <div class="user-info pa-3 mb-3 rounded-lg">
          <div class="d-flex align-center">
            <v-avatar color="primary" size="36" class="mr-3">
              <v-icon size="20" color="white">mdi-account</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <p class="text-white text-body-2 font-weight-medium mb-0">
                {{ userName }}
              </p>
              <div class="d-flex align-center gap-1">
                <v-chip
                  :color="isCashier ? 'blue-lighten-3' : 'green-lighten-3'"
                  size="x-small"
                  variant="flat"
                  class="text-capitalize px-2"
                >
                  {{ userRole }}
                </v-chip>
              </div>
            </div>
          </div>
        </div>

        <v-btn
          variant="outlined"
          color="white"
          size="small"
          prepend-icon="mdi-logout"
          block
          @click="handleLogout"
          :loading="isLoggingOut"
          class="logout-btn"
        >
          Logout
        </v-btn>
      </div>
    </template>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import type { NavigationItem } from '@/types'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/useAuthStore'
import { ROLES } from '@/router'

interface Props {
  drawer: boolean
  navigationItems: NavigationItem[]
}

interface Emits {
  (e: 'update:drawer', value: boolean): void
  (e: 'navigate', route: string): void
  (e: 'logout'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const route = useRoute()
const authStore = useAuthStore()

const isLoggingOut = ref(false)
const isMobile = ref(false)

const checkMobile = () => {
  isMobile.value = window.innerWidth < 960
}

onMounted(() => {
  checkMobile()
  window.addEventListener('resize', checkMobile)
})

onUnmounted(() => {
  window.removeEventListener('resize', checkMobile)
})

const localDrawer = computed({
  get: () => props.drawer,
  set: (value) => emit('update:drawer', value),
})

const userName = computed(() => authStore.user?.username || 'User')
const userRole = computed(() => authStore.user?.role || 'User')
const isCashier = computed(() => authStore.user?.role?.toLowerCase() === ROLES.CASHIER)

// ─── Role-aware nav item definitions ─────────────────────────────────────────
// Each item declares which roles can see it. If `roles` is omitted, all see it.
interface NavItemWithRoles extends NavigationItem {
  roles?: string[]
}

const allNavigationItems = computed<NavItemWithRoles[]>(() => [
  {
    title: 'Dashboard',
    value: 'dashboard',
    icon: 'mdi-view-dashboard-outline',
    // Both roles
  },
  {
    title: 'POS',
    value: 'POS',
    icon: 'mdi-point-of-sale',
    // Both roles
  },
  {
    title: 'Sales',
    value: 'sales',
    icon: 'mdi-cash-register',
    // Both roles — cashier sees only their own (filtered in component)
  },
  {
    title: 'EIS Inventory Sync',
    icon: 'mdi-sync',
    value: 'eis-inventory-sync',
    // Both roles
  },

  // ── Admin only ──────────────────────────────────────────────────────────────
  {
    title: 'EIS Stock',
    value: 'eis-stock',
    icon: 'mdi-package-variant',
    roles: [ROLES.ADMIN],
  },
  {
    title: 'Terminal Management',
    icon: 'mdi-monitor-multiple',
    value: 'eis-terminal-management',
    roles: [ROLES.ADMIN],
  },
  {
    title: 'Stock Reconciliation',
    icon: 'mdi-clipboard-check',
    value: 'stock-reconciliation',
    roles: [ROLES.ADMIN],
  },
  {
    title: 'Adjustment History',
    icon: 'mdi-swap-horizontal',
    value: 'AdjustmentHistory',
    roles: [ROLES.ADMIN],
  },
  {
    title: 'Customers',
    icon: 'mdi-account-group',
    value: 'customers',
    roles: [ROLES.ADMIN],
  },
  {
    title: 'Invoices',
    icon: 'mdi-file-document',
    value: 'InvoiceList',
    roles: [ROLES.ADMIN],
  },
  {
    title: 'Users',
    value: 'users',
    icon: 'mdi-account-group-outline',
    roles: [ROLES.ADMIN],
  },
  // {
  //   title: 'Terminal Settings',
  //   icon: 'mdi-office-building-cog',
  //   value: 'company-configurations',
  //   roles: [ROLES.ADMIN],
  // },
])

// Filter items based on the current user's role
const filteredNavigationItems = computed(() => {
  const role = authStore.user?.role?.toLowerCase()
  return allNavigationItems.value.filter((item) => {
    if (!item.roles || item.roles.length === 0) return true
    return role && item.roles.includes(role)
  })
})

const isActive = (routeName: string): boolean => route.name === routeName

const handleNavigate = (routeName: string) => {
  emit('navigate', routeName)
  if (isMobile.value) {
    localDrawer.value = false
  }
}

const handleLogout = async () => {
  isLoggingOut.value = true
  try {
    emit('logout')
  } finally {
    setTimeout(() => {
      isLoggingOut.value = false
    }, 1000)
  }
}
</script>

<style scoped>
.sidebar {
  background: linear-gradient(180deg, #28568b 0%, #1976d2 100%) !important;
  box-shadow: 2px 0 8px rgba(0, 0, 0, 0.1);
}

.logo-section {
  background: rgba(255, 255, 255);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255);
}

.nav-item {
  transition: all 0.2s ease;
  padding: 12px 16px !important;
}

.nav-item:hover {
  background: rgba(255, 255, 255, 0.1) !important;
}

.active-item {
  background: rgba(255, 255, 255, 0.15) !important;
  border-left: 3px solid #ffffff;
}

.user-info {
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.logout-btn {
  border-color: rgba(255, 255, 255, 0.3) !important;
  transition: all 0.2s ease;
}

.logout-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

:deep(.v-list-item-title) {
  font-size: 0.9rem;
}
</style>
